import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Train, Car, Bike, Navigation, SquareParking, Info } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd, venueLd } from "../components/SEO";

const ICONS = { transportes: Train, carro: Car, uber: Navigation, bicicleta: Bike };

export default function LocalPage() {
    const { t } = useLanguage();
    useSEO({
        title: t.seo.local.title,
        description: t.seo.local.desc,
        jsonLd: [
            breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.local, path: "/local" }]),
            venueLd,
        ],
    });
    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative" data-testid="local-page">
            {/* Fundo fixo: foto noturna do Centro Papa Giovanni; a informação rola por cima */}
            <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
                <img loading="eager" decoding="async" src="/images/local-noite.webp" alt="" className="w-full h-full object-cover object-center" data-testid="local-fundo" />
                <div className="absolute inset-0 bg-[#070503]/50" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-10 pt-56 sm:pt-48 pb-24"
            >
                <div className="max-w-2xl">
                    <Reveal delay={0.15}>
                        <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{t.local.label}</p>
                        <h1 className="font-display font-medium gold-text text-3xl sm:text-4xl tracking-[0.06em] leading-tight mt-3" data-testid="local-page-title">
                            {t.local.titulo}
                        </h1>
                        <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-32 opacity-90 my-4" />
                        {/* Caixa com a mesma transparência dos cartões de acessos abaixo */}
                        <div className="border border-[#c9a24b]/20 bg-[#0a0705]/70 backdrop-blur-sm px-6 py-5 sm:px-8 sm:py-6" data-testid="local-intro-placa">
                            <p className="font-display font-light text-xl text-[#efe6d6]/85 leading-relaxed" data-testid="local-intro">
                                {t.local.intro}
                            </p>
                            <div className="h-px bg-[#c9a24b]/25 my-4" aria-hidden="true" />
                            <p className="font-caps text-[9px] tracking-[0.3em] uppercase text-[#d19a3f]">{t.localizacao.moradaLabel}</p>
                            <p className="font-display font-medium text-lg gold-text mt-1.5 tracking-[0.02em]" data-testid="local-morada">
                                {t.local.morada}
                            </p>
                        </div>
                    </Reveal>
                </div>

                <Reveal className="mt-20">
                    <div className="flex items-center justify-center gap-4 sm:gap-5">
                        <span className="hidden sm:block h-px flex-1 max-w-56 bg-gradient-to-l from-[#c9a24b]/60 to-transparent" />
                        <span className="hidden sm:block w-1.5 h-1.5 rotate-45 border border-[#c9a24b]" />
                        <h2 className="font-caps gold-text text-lg sm:text-xl tracking-[0.2em] text-center" data-testid="como-chegar-title">
                            {t.localizacao.acessosTitulo}
                        </h2>
                        <span className="hidden sm:block w-1.5 h-1.5 rotate-45 border border-[#c9a24b]" />
                        <span className="hidden sm:block h-px flex-1 max-w-56 bg-gradient-to-r from-[#c9a24b]/60 to-transparent" />
                    </div>
                    <div className="flex justify-center mt-2">
                        <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-44 opacity-90" />
                    </div>
                </Reveal>

                <div className="mt-12 grid sm:grid-cols-2 gap-6">
                    {t.local.cards.map((c, i) => {
                        const Icon = ICONS[c.id];
                        return (
                            <Reveal key={c.id} delay={i * 0.08}>
                                <div className="border border-[#c9a24b]/20 bg-[#0a0705]/70 backdrop-blur-sm p-8 h-full" data-testid={`como-chegar-${c.id}`}>
                                    <Icon size={30} strokeWidth={1.2} className="text-[#d19a3f]" />
                                    <h3 className="font-caps text-sm tracking-[0.2em] uppercase text-[#efe6d6] mt-5">{c.title}</h3>
                                    <p className="text-sm sm:text-base font-light text-[#efe6d6]/65 leading-relaxed mt-3">{c.text}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal className="mt-10">
                    <div className="border border-[#c9a24b]/30 bg-[#0a0705]/70 backdrop-blur-sm p-8" data-testid="estacionamento-hager">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 border border-[#c9a24b]/40 flex items-center justify-center text-[#c9a24b] shrink-0">
                                <SquareParking size={24} strokeWidth={1.3} />
                            </div>
                            <h2 className="font-caps text-sm sm:text-base tracking-[0.25em] uppercase gold-text" data-testid="estacionamento-hager-titulo">{t.local.estacionamentoHager.titulo}</h2>
                        </div>
                        <p className="mt-5 text-sm sm:text-base font-light text-[#efe6d6]/75 leading-relaxed" data-testid="estacionamento-hager-intro">
                            {t.local.estacionamentoHager.intro}
                        </p>
                        <img
                            loading="lazy"
                            decoding="async"
                            src="/images/mapa-estacionamento.webp"
                            alt={t.local.estacionamentoHager.titulo}
                            className="mt-6 w-full border border-[#c9a24b]/25"
                            data-testid="estacionamento-hager-mapa"
                        />
                        <ul className="mt-5 space-y-3">
                            {t.local.estacionamentoHager.pontos.map((p, i) => (
                                <li key={i} className="flex gap-3 items-start" data-testid={`estacionamento-hager-ponto-${i}`}>
                                    <span className="mt-2.5 h-px w-5 bg-[#c9a24b]/60 shrink-0" aria-hidden="true" />
                                    <span className="text-sm font-light text-[#efe6d6]/70 leading-relaxed">{p}</span>
                                </li>
                            ))}
                        </ul>
                        <a
                            href="https://www.google.com/maps/search/?api=1&query=47.0698242,8.2872323"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="estacionamento-hager-maps"
                            className="inline-block mt-6 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-6 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400"
                        >
                            {t.local.estacionamentoHager.mapsCta}
                        </a>
                    </div>
                </Reveal>

                <Reveal className="mt-6">
                    <Link
                        to="/experiencia?tab=bar"
                        data-testid="local-bar-card"
                        className="flex flex-col sm:flex-row items-center justify-between gap-4 border border-[#c9a24b]/20 bg-[#0a0705]/70 backdrop-blur-sm px-8 py-6 hover:border-[#c9a24b]/60 transition-colors duration-400"
                    >
                        <div className="text-center sm:text-left">
                            <p className="font-caps text-xs tracking-[0.25em] uppercase text-[#efe6d6]" data-testid="local-bar-titulo">{t.local.barTitulo}</p>
                            <p className="text-sm font-light text-[#efe6d6]/65 leading-relaxed mt-2" data-testid="local-bar-texto">{t.local.barTexto}</p>
                        </div>
                        <span className="shrink-0 font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b]">{t.experiencia.lineupLink}</span>
                    </Link>
                </Reveal>

                <Reveal className="mt-6">
                    <div className="border border-[#c9a24b]/30 bg-[#0a0705]/70 backdrop-blur-sm px-8 py-6 flex gap-5 items-start" data-testid="local-regras">
                        <div className="w-11 h-11 border border-[#c9a24b]/50 flex items-center justify-center text-[#c9a24b] shrink-0">
                            <Info size={20} strokeWidth={1.4} />
                        </div>
                        <div>
                            <p className="font-caps text-xs sm:text-sm tracking-[0.25em] uppercase gold-text" data-testid="local-regras-titulo">{t.localizacao.regrasTitulo}</p>
                            <ul className="mt-3 space-y-2">
                                {t.localizacao.regras.map((r, i) => (
                                    <li key={i} className="flex gap-3 items-start" data-testid={`local-regra-${i}`}>
                                        <span className="mt-2.5 h-px w-4 bg-[#c9a24b]/70 shrink-0" aria-hidden="true" />
                                        <span className="text-sm font-light text-[#efe6d6]/85 leading-relaxed">{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>

                <Reveal className="mt-14">
                    <a
                        href="https://www.google.com/maps/search/?api=1&query=47.0705647,8.2848735"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="local-page-maps-cta"
                        className="flex flex-col sm:flex-row items-center gap-6 border border-[#c9a24b]/40 bg-[#0a0705]/60 backdrop-blur-sm px-8 py-6 hover:border-[#c9a24b] hover:bg-[#c9a24b]/10 transition-colors duration-400"
                    >
                        <img src="/images/qr-local.webp" alt="QR code para o Google Maps" className="w-20 h-20 shrink-0" />
                        <div className="text-center sm:text-left">
                            <p className="font-caps text-xs tracking-[0.25em] uppercase text-[#c9a24b]">{t.local.mapsCta}</p>
                            <p className="font-display font-light text-lg text-[#efe6d6]/75 mt-2 leading-relaxed">
                                {t.local.mapsTexto}
                            </p>
                        </div>
                    </a>
                </Reveal>

                <Reveal className="mt-16 text-center">
                    <p className="font-display italic font-light text-2xl sm:text-3xl text-[#efe6d6]/80 leading-relaxed max-w-2xl mx-auto">
                        {t.local.fecho}
                    </p>
                </Reveal>
            </motion.main>
        </div>
    );
}
